import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatCurrencyWhole as formatCurrency } from '../../lib/format';
import { TrendingUp, TrendingDown, Minus, DollarSign, Users, ShoppingCart, Activity, Repeat, Info } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { LineChart } from '../charts/LineChart';

// Per-AM performance vs cohort. Renders KPI cards (this AM vs cohort
// p25/p50/p75) and one line chart per metric showing the AM's value over
// time with three reference lines for the cohort percentile bands.

type Interval = 'day' | 'week' | 'month';

interface Summary {
  value: number;
  cohortP25: number;
  cohortP50: number;
  cohortP75: number;
  percentile: number;
}

interface PerfData {
  am: { id: string; name: string; networkSize: number };
  interval: Interval;
  cohortSize: number;
  series: {
    bucket: string;
    revenue: number;
    amMargin: number;
    orderCount: number;
    activePhysicians: number;
    activePhysicianPct: number;
    avgCompositeOrderSize: number;
    reorderVelocity: number;
  }[];
  cohort: {
    bucket: string;
    revenue: { p25: number; p50: number; p75: number };
    amMargin: { p25: number; p50: number; p75: number };
    orderCount: { p25: number; p50: number; p75: number };
    activePhysicianPct: { p25: number; p50: number; p75: number };
    avgCompositeOrderSize: { p25: number; p50: number; p75: number };
    reorderVelocity: { p25: number; p50: number; p75: number };
  }[];
  summary: Record<string, Summary>;
}

const formatPct = (n: number) => `${(n).toFixed(1)}%`;
const formatNum = (n: number) => (Number.isInteger(n) ? n.toString() : n.toFixed(2));

const METRIC_DEFS: {
  key: keyof PerfData['summary'];
  label: string;
  icon: any;
  format: (n: number) => string;
  description: string;
}[] = [
  {
    key: 'revenue',
    label: 'Revenue',
    icon: DollarSign,
    format: formatCurrency,
    description:
      'Sum of order totals (totalAmount) across the AM\'s network for the period. Includes both patient and bulk orders. Voided and cancelled orders are excluded.'
  },
  {
    key: 'amMargin',
    label: 'AM Margin',
    icon: DollarSign,
    format: formatCurrency,
    description:
      'Σ (item.frozenPricing.distributorMarkup × quantity) across every line on every non-voided order in the AM\'s network. This is the portion of each order that flows to the AM as compensation.'
  },
  {
    key: 'activePhysicianPct',
    label: 'Active Physician %',
    icon: Users,
    format: formatPct,
    description:
      'Distinct physicians in the AM\'s network who placed at least one order in the period, divided by total network size. A high % means most of the network is actively ordering; a low % suggests dormant clinics.'
  },
  {
    key: 'avgCompositeOrderSize',
    label: 'Avg Order Size',
    icon: ShoppingCart,
    format: formatCurrency,
    description:
      'Average revenue per "composite order". All patient orders from a single clinic on the same calendar day are summed into one composite (so a clinic that places six $200 orders in a day reads as one $1,200 composite, not six $200 averages). Each bulk order counts as one composite on its own. Voided/cancelled excluded.'
  },
  {
    key: 'reorderVelocity',
    label: 'Reorder Velocity',
    icon: Repeat,
    format: formatNum,
    description:
      'Composite-order count divided by the number of active physicians in the period. Tells you how often each engaged clinic is reordering — high velocity = sticky/loyal network.'
  }
];

// Popover that opens on hover *and* on click (the latter so it works on
// touch devices and so users can read it without dancing the cursor).
// Click-outside / Escape closes it.
const InfoTip: React.FC<{ text: string; className?: string }> = ({ text, className = '' }) => {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const open = pinned || hovered;

  useEffect(() => {
    if (!pinned) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setPinned(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPinned(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [pinned]);

  return (
    <span
      ref={ref}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setPinned(p => !p); }}
        className="inline-flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-700 cursor-help"
        aria-label={text}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-full z-30 mt-2 w-72 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
};

interface KpiCardProps {
  label: string;
  icon: any;
  summary: Summary | undefined;
  format: (n: number) => string;
  description: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, icon: Icon, summary, format, description }) => {
  if (!summary) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="text-sm text-gray-500">{label}</div>
        <div className="mt-1 text-lg font-semibold text-gray-400">—</div>
      </div>
    );
  }
  // Delta vs cohort median (p50). Highlights below-average performers.
  const deltaPct =
    summary.cohortP50 > 0
      ? Math.round(((summary.value - summary.cohortP50) / summary.cohortP50) * 100)
      : null;
  const trendIcon =
    deltaPct === null ? Minus
    : deltaPct > 5 ? TrendingUp
    : deltaPct < -5 ? TrendingDown
    : Minus;
  const TrendIcon = trendIcon;
  const trendColor =
    deltaPct === null ? 'text-gray-400'
    : deltaPct > 5 ? 'text-green-600'
    : deltaPct < -5 ? 'text-red-600'
    : 'text-gray-500';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Icon className="w-4 h-4" />
          {label}
          <InfoTip text={description} />
        </div>
        <div className={`flex items-center gap-0.5 text-xs font-medium ${trendColor}`}>
          <TrendIcon className="w-3.5 h-3.5" />
          {deltaPct !== null ? `${deltaPct > 0 ? '+' : ''}${deltaPct}%` : '—'}
        </div>
      </div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{format(summary.value)}</div>
      <div className="mt-2 text-xs text-gray-500 space-y-0.5">
        <div>Average: <span className="font-medium text-gray-700">{format(summary.cohortP50)}</span></div>
        <div className="flex items-center gap-2">
          <span className="text-red-600">Underperforming {format(summary.cohortP25)}</span>
          <span>·</span>
          <span className="text-green-700">Exceptional {format(summary.cohortP75)}</span>
        </div>
        <div>Percentile rank: <span className="font-medium text-gray-700">{summary.percentile}</span></div>
      </div>
    </div>
  );
};

interface MetricChartProps {
  metric: keyof PerfData['summary'];
  label: string;
  data: PerfData;
  format: (n: number) => string;
  description: string;
}

const MetricChart: React.FC<MetricChartProps> = ({ metric, label, data, format, description }) => {
  // Combine focal-AM series + cohort bands into one row-per-bucket dataset
  // so LineChart can plot all four lines.
  const merged = useMemo(() => {
    const cohortByBucket = new Map(data.cohort.map(c => [c.bucket, c]));
    return data.series.map(s => {
      const c = cohortByBucket.get(s.bucket);
      const band = c ? (c as any)[metric] as { p25: number; p50: number; p75: number } : { p25: 0, p50: 0, p75: 0 };
      return {
        bucket: s.bucket,
        amValue: (s as any)[metric] as number,
        p25: band.p25,
        p50: band.p50,
        p75: band.p75
      };
    });
  }, [data, metric]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
        {label} over time
        <InfoTip text={description} />
      </h4>
      <LineChart
        data={merged}
        xKey="bucket"
        lines={[
          { dataKey: 'amValue', name: data.am.name, color: '#0ea5e9', strokeWidth: 3 },
          { dataKey: 'p75', name: 'Exceptional (p75)', color: '#16a34a', strokeWidth: 1, strokeDasharray: '4 4' },
          { dataKey: 'p50', name: 'Average (p50)', color: '#475569', strokeWidth: 1, strokeDasharray: '4 4' },
          { dataKey: 'p25', name: 'Underperforming (p25)', color: '#dc2626', strokeWidth: 1, strokeDasharray: '4 4' }
        ]}
        height={220}
        formatTooltip={(value: any, name: string) => [format(value), name]}
      />
    </div>
  );
};

interface AccountManagerPerformancePanelProps {
  amId: string | null;
  /** Caller hint for header copy. Cohort scoping is enforced server-side. */
  cohortHint?: 'admin' | 'supervisor';
}

const AccountManagerPerformancePanel: React.FC<AccountManagerPerformancePanelProps> = ({ amId, cohortHint }) => {
  const [data, setData] = useState<PerfData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interval, setInterval] = useState<Interval>('month');

  useEffect(() => {
    if (!amId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .getAccountManagerPerformance(amId, { interval })
      .then(res => {
        if (cancelled) return;
        if (res.success && res.data) setData(res.data as PerfData);
        else setError(res.error || 'Failed to load AM performance');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [amId, interval]);

  if (!amId) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
        Pick an Account Manager above to see their performance vs cohort.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-400">
        Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error || 'No data'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            {data.am.name}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Network: {data.am.networkSize} physicians · Cohort size: {data.cohortSize}
            {cohortHint && ` (${cohortHint === 'admin' ? 'all AMs platform-wide' : 'your AMs'})`}
          </p>
        </div>
        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
          {(['day', 'week', 'month'] as const).map(opt => (
            <button
              key={opt}
              onClick={() => setInterval(opt)}
              className={`px-3 py-1.5 ${interval === opt ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {opt[0].toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {METRIC_DEFS.map(def => (
          <KpiCard
            key={def.key as string}
            label={def.label}
            icon={def.icon}
            summary={data.summary[def.key as string]}
            format={def.format}
            description={def.description}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {METRIC_DEFS.map(def => (
          <MetricChart
            key={def.key as string}
            metric={def.key}
            label={def.label}
            data={data}
            format={def.format}
            description={def.description}
          />
        ))}
      </div>
    </div>
  );
};

export default AccountManagerPerformancePanel;
